//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { IPromise } from 'angular';
import {
  IHrefOptions,
  IState,
  IStateOptions,
  IStateService,
} from 'angular-ui-router';

import getAngularInjector from '@react-bridge/getAngularInjector';
import { RoutableStateName } from 'app/top-level-states';
import { StateParams } from 'app/initialization/routing/stateParamModels';
import { TypedRoutableStateName } from 'app/initialization/routing/stateParamModels';

const ANGULAR_STATE_SERVICE_NAME = '$state';

type NavigateStateParams<T extends RoutableStateName> =
  T extends TypedRoutableStateName ? StateParams<T> : {};

type NavigateStateName<T extends RoutableStateName> =
  T extends TypedRoutableStateName ? T : RoutableStateName;

type NavigateParams<T extends RoutableStateName> =
  T extends TypedRoutableStateName
    ? [
        stateName: NavigateStateName<T>,
        stateParams: NavigateStateParams<T>,
        options?: IStateOptions,
      ]
    : [
        stateName: NavigateStateName<T>,
        stateParams?: NavigateStateParams<T>,
        options?: IStateOptions,
      ];

export function navigateToState<T extends RoutableStateName>(
  ...[stateName, stateParams, options]: NavigateParams<T>
): IPromise<any> {
  const angularStateService: IStateService = getAngularInjector().get(
    ANGULAR_STATE_SERVICE_NAME,
  );
  return angularStateService.go(stateName, stateParams, options);
}

export function reloadState(state?: RoutableStateName | IState): IPromise<any> {
  const angularStateService: IStateService = getAngularInjector().get(
    ANGULAR_STATE_SERVICE_NAME,
  );
  return angularStateService.reload(state);
}

export function getStateName(): RoutableStateName {
  const angularStateService: IStateService = getAngularInjector().get(
    ANGULAR_STATE_SERVICE_NAME,
  );
  return angularStateService.current.name as RoutableStateName;
}

export function getStateParams<T extends Record<string, any> = unknown>(): T {
  const angularStateService: IStateService = getAngularInjector().get(
    ANGULAR_STATE_SERVICE_NAME,
  );
  return angularStateService.params as T;
}

export function getStateHref(
  stateName: RoutableStateName,
  stateParams = {},
  options: IHrefOptions = {},
): string {
  const angularStateService: IStateService = getAngularInjector().get(
    ANGULAR_STATE_SERVICE_NAME,
  );
  return angularStateService.href(stateName, stateParams, options);
}
